<template>
  <f7-page name="launch" class="launch-page" :navbar="false">
    <div class="launch-container">
      <div class="launch-card">
        <img
          src="@/assets/poolit-logo.svg"
          alt="Poolit"
          class="launch-logo"
        />

        <div v-if="state === 'loading'" class="launch-state">
          <f7-preloader :size="48" color="white"></f7-preloader>
          <h2 class="launch-title">
            {{ $t('auth.launch.browserSigningIn') }}
          </h2>
          <p class="launch-subtitle">
            {{ $t('auth.launch.browserWelcome') }}
          </p>
        </div>

        <div v-else-if="state === 'success'" class="launch-state">
          <f7-icon
            f7="checkmark_circle_fill"
            size="64px"
            color="white"
          ></f7-icon>
          <h2 class="launch-title">
            {{ $t('auth.loginSuccess') }}
          </h2>
        </div>

        <div v-else-if="state === 'error'" class="launch-state launch-state--error">
          <f7-icon
            f7="exclamationmark_triangle_fill"
            size="56px"
            color="white"
          ></f7-icon>
          <h2 class="launch-title">
            {{ errorTitle }}
          </h2>
          <p class="launch-subtitle">
            {{ errorBody }}
          </p>
          <div class="launch-actions">
            <f7-button
              v-if="canRetry"
              large
              fill
              class="launch-button launch-button--primary"
              @click="retry"
            >
              {{ $t('auth.launch.retry') }}
            </f7-button>
            <f7-button
              large
              outline
              class="launch-button launch-button--secondary"
              @click="openTelegram"
            >
              {{ $t('auth.launch.openTelegram') }}
            </f7-button>
          </div>
        </div>
      </div>
    </div>
  </f7-page>
</template>

<script setup>
import { f7 } from 'framework7-vue';
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { launchLandingPath } from '@/clubs/manifest.js';
import { collectorHoldActive } from '@/collector/session.js';
import { useAuth, useAuthInitialize } from '@/composables/useAuth';
import { usePwaInstall } from '@/composables/usePwaInstall';
import { exchange as exchangeToken } from '@/services/launchToken.service';
import { useAuthStore } from '@/stores/authStore';
import { openTelegramBot } from '@/utils/launchTarget.js';

const props = defineProps({
  f7router: {
    type: Object,
    default: null,
  },
  f7route: {
    type: Object,
    default: null,
  },
});

const { t } = useI18n();
const authStore = useAuthStore();
const { logout } = useAuth();
const { initialize: initAuth } = useAuthInitialize();
const { requestInstall } = usePwaInstall();

const state = ref('loading');
const errorKind = ref(null);
const lastToken = ref(null);

// Only network failures are safe to retry — any 4xx response burns the
// single-use token, so replaying would just yield "Link expired".
const canRetry = computed(() => errorKind.value === 'network');

const errorTitle = computed(() => {
  if (errorKind.value === 'expired' || errorKind.value === 'invalid') {
    return t('auth.launch.tokenExpired');
  }
  return t('auth.launch.error');
});

const errorBody = computed(() => {
  if (errorKind.value === 'expired' || errorKind.value === 'invalid') {
    return t('auth.launch.tokenExpiredBody');
  }
  return t('auth.launch.browserError');
});

onMounted(async () => {
  const token = readToken();
  lastToken.value = token;

  if (token) {
    stripTokenFromUrl();
  }

  // Re-entry guard: if the user is already authenticated (e.g. came back via
  // history, bookmark, or shared link), don't replay the single-use token —
  // it would 410 and surface a misleading "Link expired" message. Except a
  // collector held on his day after an expired login (collector-app P2-B,
  // L-D): a fresh token is exactly what he came for.
  if (authStore.isAuthenticated && !(token && collectorHoldActive())) {
    routeByExistingAuth();
    return;
  }

  if (!token) {
    routeByExistingAuth();
    return;
  }

  await runExchangeAndAuth(token);
});

function readToken() {
  try {
    const raw = new URLSearchParams(window.location.search).get('t');
    return raw && raw.trim() ? raw : null;
  } catch {
    return null;
  }
}

function stripTokenFromUrl() {
  try {
    // Preserve trailing slash — F7 v9's path-to-regexp v6 treats /launch and
    // /launch/ asymmetrically, and the trailing-slash redirect block in
    // routes.js relies on the strict form matching first.
    window.history.replaceState({}, '', '/launch/');
  } catch {
    /* replaceState can throw in obscure embedded contexts; not fatal */
  }
}

function routeByExistingAuth() {
  const router = resolveRouter();
  if (!router) return;

  if (authStore.isAuthenticated) {
    router.navigate(landingPath(), { clearPreviousHistory: true });
  } else {
    router.navigate('/login/', { clearPreviousHistory: true });
  }
}

async function runExchangeAndAuth(token) {
  state.value = 'loading';
  errorKind.value = null;

  try {
    await exchangeToken({ token });
  } catch (err) {
    if (err?.status === 410) errorKind.value = 'expired';
    else if (err?.status === 400) errorKind.value = 'invalid';
    else if (err?.status === 0) errorKind.value = 'network';
    else errorKind.value = 'unknown';
    state.value = 'error';
    return;
  }

  // Cookie is set. Force priority-3 (cookie-bootstrap) by clearing any stale
  // persisted session first. Without this, a leftover localStorage blob from
  // a prior account on the same device would short-circuit priority 2 and
  // skip the fresh /profile fetch — store would mirror the wrong user.
  authStore.clearSession();

  // Re-run the auth init pipeline; priority 3 hits, fetches profile, hydrates
  // useAuthStore. Same path the server-side OIDC return uses today.
  const asyncState = initAuth();
  if (!asyncState.isReady.value) {
    await new Promise((resolve) => {
      const stop = watch(
        asyncState.isReady,
        (ready) => {
          if (ready) {
            stop();
            resolve();
          }
        },
        { immediate: true },
      );
    });
  }

  if (!authStore.isAuthenticated) {
    // Cookie set but profile fetch failed (e.g. 401, server error). The cookie
    // is now suspect — clear it via the logout endpoint so the user isn't
    // stuck in a half-authed state where /login redirects them back in.
    try {
      await logout();
    } catch {
      /* logout best-effort */
    }
    errorKind.value = 'unknown';
    state.value = 'error';
    return;
  }

  state.value = 'success';

  // Brief beat so users register the success state before navigation.
  await new Promise((resolve) => setTimeout(resolve, 400));

  const router = resolveRouter();
  if (router) router.navigate(landingPath(), { clearPreviousHistory: true });

  // Ask for the install card. The nudge scheduler decides (club trigger,
  // snooze, the collector's day, one overlay at a time), so this is safe
  // post-navigation and a duplicate of AppLayout's boot request is ignored.
  requestInstall('launch');
}

// A collector with no club membership lands on his day, not /home/ (which
// would bounce him to onboarding): the Telegram-only collectors reach Android
// Chrome through this handoff (collector-app phase 2 research, Section 2).
// Everyone else — leaders and the admin who are also members — keeps /home/.
function landingPath() {
  return launchLandingPath({
    isCollector: authStore.isCollector,
    hasActiveMembership: authStore.hasActiveMembership,
  });
}

function retry() {
  // Single-use tokens are consumed by a 4xx response too, so only allow
  // replay when the request never reached the server (network failure).
  // Any other failure mode → user must get a fresh token from Telegram.
  if (errorKind.value === 'network' && lastToken.value) {
    runExchangeAndAuth(lastToken.value);
    return;
  }
  openTelegram();
}

function openTelegram() {
  openTelegramBot({
    // No bot username configured — fall back to login screen.
    fallback: () => resolveRouter()?.navigate('/login/', { clearPreviousHistory: true }),
  });
}

function resolveRouter() {
  if (props.f7router) return props.f7router;
  return f7?.views?.main?.router ?? null;
}
</script>

<style scoped>
.launch-page {
  --f7-page-bg-color: transparent;
}

/* Safe-area aware padding — without `env(safe-area-inset-*)` the bottom CTA
   on the error state lands within ~10px of the iOS home gesture bar on
   notched devices in PWA standalone mode. `min-height: 100%` keeps
   the gradient covering the viewport without a `vh`-family unit (this element
   is `position: fixed`, so the percentage resolves against the viewport, not
   the app root — the html.telegram-webapp contract in app.css does not reach
   it, and `inset: 0` already does the sizing).
   Mirrors TgLaunchPage. */
.launch-container {
  position: fixed;
  inset: 0;
  min-height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding:
    max(1.5rem, env(safe-area-inset-top))
    max(1.5rem, env(safe-area-inset-right))
    max(1.5rem, env(safe-area-inset-bottom))
    max(1.5rem, env(safe-area-inset-left));
  background: linear-gradient(135deg, #4f46e5 0%, #9333ea 50%, #ec4899 100%);
  color: #ffffff;
  z-index: 1;
}

.launch-card {
  width: 100%;
  max-width: 420px;
  text-align: center;
  animation: fadeInUp 0.5s ease-out;
}

.launch-logo {
  width: 160px;
  height: auto;
  filter: drop-shadow(0 4px 16px rgba(0, 0, 0, 0.25));
  margin-bottom: 2rem;
}

/* Compact logo on short screens (iPhone SE class). */
@media (max-height: 600px) {
  .launch-logo {
    width: 128px;
    margin-bottom: 1.25rem;
  }
}

.launch-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

/* Short horizontal shake on transition into error state — mirrors
   TgLaunchPage so the two screens feel like one continuous flow. */
.launch-state--error {
  animation: shake 0.35s ease-out;
}

.launch-title {
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

/* Opacity removed (was 0.92) — failed WCAG AA on the pink gradient stop.
   text-shadow preserves legibility on lighter gradient regions. */
.launch-subtitle {
  margin: 0;
  font-size: 15px;
  font-weight: 400;
  line-height: 1.5;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
}

.launch-actions {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 1rem;
}

.launch-button {
  height: 50px;
  border-radius: 14px;
  font-size: 16px;
  font-weight: 600;
}

/* Force opaque white surface with indigo text. Same rationale as
   TgLaunchPage: F7 v9 iOS-26 theme's frosted-fill rendering of
   `color="white"` produced a translucent purple button on some hosts
   (Telegram Desktop, dark Telegram themes). Explicit CSS pins it. */
.launch-button.launch-button--primary {
  --f7-button-bg-color: #ffffff;
  --f7-button-text-color: #4f46e5;
  --f7-button-pressed-bg-color: #eef0ff;
  --f7-button-pressed-text-color: #4f46e5;
  background: #ffffff !important;
  background-color: #ffffff !important;
  color: #4f46e5 !important;
  border: none !important;
}

.launch-button.launch-button--primary:active,
.launch-button.launch-button--primary.active-state {
  background-color: #eef0ff !important;
  color: #4f46e5 !important;
}

.launch-button.launch-button--primary :deep(.icon),
.launch-button.launch-button--primary :deep(.f7-icons),
.launch-button.launch-button--primary :deep(.material-icons),
.launch-button.launch-button--primary :deep(.preloader) {
  color: #4f46e5 !important;
}

/* Outlined secondary CTA — white border + white text, fully opaque so it
   reads on every part of the gradient and against any host theme. */
.launch-button.launch-button--secondary {
  --f7-button-text-color: #ffffff;
  --f7-button-border-color: #ffffff;
  --f7-button-pressed-bg-color: rgba(255, 255, 255, 0.15);
  color: #ffffff !important;
  border: 1.5px solid #ffffff !important;
  background: transparent !important;
}

.launch-button.launch-button--secondary:active,
.launch-button.launch-button--secondary.active-state {
  background-color: rgba(255, 255, 255, 0.15) !important;
  color: #ffffff !important;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  20% { transform: translateX(-6px); }
  40% { transform: translateX(6px); }
  60% { transform: translateX(-4px); }
  80% { transform: translateX(4px); }
}
</style>
